import React, { RefObject, useMemo } from 'react'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { t } from '../../i18n'
import { DosingLimits, Range } from '../shared/dosingLimits'
import FormSection from '../shared/forms/FormSection'
import { FormReadOnlyContext } from '../shared/forms/FormReadOnlyContext'
import { boundedNumber, whenEnabled } from '../shared/forms/yupNumbers'
import {
  SelectField,
  TextField,
  TextareaField
} from '../shared/forms/FormFields'
import { InputField } from './fields'
import {
  FILL_LEVEL_OPTIONS,
  modeLabel,
  paramLabel,
  placeholderLabel
} from './labels'
import {
  BaseLevelParameters,
  EmergencyLevelParameters
} from './LevelParametersTable'
import { SauceDosingModeValues, isNumber } from './types'

const message = (rule: string, options: Record<string, string | number> = {}) =>
  t(`sauce_dosing_modes.validations.${rule}`, options)

const rangeMessages = (field: string, { min, max }: Range) => ({
  required: message('required', { field }),
  min: message('min', { field, min }),
  max: message('max', { field, max })
})

const bounded = (field: string, range: Range) =>
  boundedNumber(range, rangeMessages(field, range))

const boundedWholeNumber = (field: string, range: Range) =>
  boundedNumber(range, {
    ...rangeMessages(field, range),
    integer: message('integer', { field })
  })

const emergencyParam = (schema: Yup.Schema) =>
  whenEnabled('emergency_enabled', schema)

const levelSchema = (limits: DosingLimits['sauce']) =>
  Yup.object({
    fast_pressure: bounded(paramLabel('fast_pressure'), limits.pressure),
    slow_pressure: bounded(paramLabel('slow_pressure'), limits.pressure),
    stop_advance_grams: boundedWholeNumber(
      paramLabel('stop_advance_grams'),
      limits.stop_advance_grams
    ),
    max_dosing_duration_ms: boundedWholeNumber(
      paramLabel('max_dosing_duration_ms'),
      limits.max_dosing_duration_ms
    ),
    no_change_max_time_ms: boundedWholeNumber(
      paramLabel('no_change_max_time_ms'),
      limits.no_change_max_time_ms
    ),
    no_change_amount: boundedWholeNumber(
      paramLabel('no_change_amount'),
      limits.no_change_amount
    ),
    emergency_strategy: Yup.string().when('emergency_enabled', {
      is: true,
      then: (schema) =>
        schema.required(
          message('required', { field: paramLabel('emergency_strategy') })
        )
    }),
    emergency_pressure: emergencyParam(
      bounded(paramLabel('emergency_pressure'), limits.emergency_pressure)
    ),
    max_emergency_duration_ms: emergencyParam(
      boundedWholeNumber(
        paramLabel('max_emergency_duration_ms'),
        limits.max_emergency_duration_ms
      )
    ),
    emergency_residual_delivery_amount: emergencyParam(
      boundedWholeNumber(
        paramLabel('emergency_residual_delivery_amount'),
        limits.emergency_residual_delivery_amount
      )
    )
  })

// The limits come from the server so the form checks exactly what the models enforce
const buildValidationSchema = (limits: DosingLimits) =>
  Yup.object({
    name: Yup.string().required(
      message('required', { field: modeLabel('name') })
    ),
    high_fill_min_percent: boundedWholeNumber(
      modeLabel('high_fill_min_percent'),
      limits.fill_percent
    ),
    medium_fill_min_percent: boundedWholeNumber(
      modeLabel('medium_fill_min_percent'),
      limits.fill_percent
    ).when('high_fill_min_percent', {
      is: isNumber,
      then: (schema) =>
        schema.lessThan(
          Yup.ref('high_fill_min_percent'),
          message('medium_below_high')
        )
    }),
    unknown_fill_fallback: Yup.string().required(
      message('unknown_fill_fallback')
    ),
    max_acceptable_shortfall_percent: boundedWholeNumber(
      modeLabel('max_acceptable_shortfall_percent'),
      limits.shortfall_percent
    ),
    levels: Yup.array().of(levelSchema(limits.sauce))
  })

export const FORM_ID = 'sauce-dosing-mode-form'

interface Props {
  initialValues: SauceDosingModeValues
  readOnly: boolean
  formikRef: RefObject<FormikProps<SauceDosingModeValues>>
  limits: DosingLimits
  onSubmit: (values: SauceDosingModeValues) => Promise<void>
}

const SauceDosingModeForm = ({
  initialValues,
  readOnly,
  formikRef,
  limits,
  onSubmit
}: Props) => {
  const validationSchema = useMemo(
    () => buildValidationSchema(limits),
    [limits]
  )

  return (
    <Formik<SauceDosingModeValues>
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit }) => (
        <form
          id={FORM_ID}
          onSubmit={handleSubmit}
          noValidate
          className="form-robis dosing-mode-form"
        >
          <FormReadOnlyContext.Provider value={readOnly}>
            <TextField
              name="name"
              label={modeLabel('name')}
              placeholder={modeLabel('enter_name')}
            />
            <TextareaField
              name="description"
              label={modeLabel('description')}
              placeholder={modeLabel('enter_description')}
            />

            <FormSection
              title={modeLabel('fill_boundaries')}
              tooltip={modeLabel('fill_boundaries_info')}
            >
              <div className="dosing-mode-form__fields">
                <InputField
                  name="high_fill_min_percent"
                  label={modeLabel('high_fill_min_percent')}
                  placeholder={placeholderLabel('high_fill_min_percent')}
                  unit="%"
                />
                <InputField
                  name="medium_fill_min_percent"
                  label={modeLabel('medium_fill_min_percent')}
                  placeholder={placeholderLabel('medium_fill_min_percent')}
                  unit="%"
                />
                <SelectField
                  name="unknown_fill_fallback"
                  label={modeLabel('unknown_fill_fallback')}
                  tooltip={modeLabel('unknown_fill_fallback_info')}
                  options={FILL_LEVEL_OPTIONS}
                  placeholder={modeLabel('select_unknown_fill_fallback')}
                  inverted
                />
              </div>
            </FormSection>

            <div className="dosing-mode-form__panel">
              <BaseLevelParameters />
            </div>

            <div className="dosing-mode-form__panel">
              <EmergencyLevelParameters />
            </div>

            <FormSection
              title={modeLabel('partial_completion')}
              hint={modeLabel('partial_completion_hint')}
            >
              <div className="dosing-mode-form__fields dosing-mode-form__fields--narrow">
                <InputField
                  name="max_acceptable_shortfall_percent"
                  label={modeLabel('max_acceptable_shortfall_percent')}
                  unit="%"
                />
              </div>
            </FormSection>
          </FormReadOnlyContext.Provider>
        </form>
      )}
    </Formik>
  )
}

export default SauceDosingModeForm
